import { MarkerClusterer, Renderer } from '@googlemaps/markerclusterer';
import { SiteIdType, SiteTheme, createHref } from '@klokgroep/sanity';
import { useCommonTranslations } from '@klokgroep/shared-components/utils/useTranslations';
import { useSiteInfo } from '@klokgroep/shared-components/src/SiteInfoProvider';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import cx from 'classnames';
import React, { useEffect, useRef, ReactElement, useMemo } from 'react';
import styles from './Map.module.css';

interface Properties {
  center: MapComponentProperties['center'];
  markers: MapComponentProperties['markers'];
  showOnMobile?: boolean;
  styleString?: string;
  zoom: MapComponentProperties['zoom'];
}

export const Map = ({ markers, center, zoom, showOnMobile, styleString }: Properties) => {
  const classNames = cx(styles.container, { [styles.showOnMobile]: showOnMobile });
  return (
    <div className={classNames}>
      <Wrapper apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string} render={renderGoogleMaps}>
        <MapComponent markers={markers} center={center} styleString={styleString} zoom={zoom} />
      </Wrapper>
    </div>
  );
};

const renderGoogleMaps = (status: Status): ReactElement => {
  if (status === Status.LOADING) return <h3>{status} ..</h3>;
  if (status === Status.FAILURE) return <h3>{status} ...</h3>;
  return <React.Fragment> </React.Fragment>;
};

interface MapComponentProperties {
  center: google.maps.LatLngLiteral;
  styleString?: string;
  zoom: number;
  markers?: {
    contactDetails?: { phone?: string; email?: string };
    description?: string;
    imageUrl?: string;
    lat?: number;
    lng?: number;
    locationName?: string;
    name?: string;
    postAddress?: { mailBox?: string; postalCode?: string; city?: string };
    siteId?: SiteIdType;
    slug?: string;
    visitingAddress?: { streetAndNumber?: string; postalCode?: string; city?: string };
  }[];
}

const MapComponent = ({ center, markers, zoom, styleString }: MapComponentProperties) => {
  const { locale, theme } = useSiteInfo();
  const t = useCommonTranslations();

  const domElementReference = useRef(null);
  const mapReference = useRef<google.maps.Map>();
  const markersReference = useRef<google.maps.Marker[]>();

  const infoWindow = useRef(new google.maps.InfoWindow({}));

  const googleMapStyles = useMemo(() => (styleString ? JSON.parse(styleString) : undefined), [styleString]);

  useEffect(() => {
    if (domElementReference.current) {
      mapReference.current = new window.google.maps.Map(domElementReference.current, {
        center,
        zoom,
        disableDefaultUI: true,
        styles: googleMapStyles,
      });
    }
  }, [center, googleMapStyles, zoom]);

  useEffect(() => {
    if (mapReference.current) {
      const markerIconEmpty = getMarkerIconForThemeAndSiteId(theme, 'holding', true);

      const clusterRenderer: Renderer = {
        render: ({ count, position }) =>
          new window.google.maps.Marker({
            label: { text: String(count), color: 'white', fontSize: '18px', fontWeight: 'bold' },
            position,
            icon: markerIconEmpty,
            zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
          }),
      };

      markersReference.current = markers
        ?.map((marker) => {
          const markerIcon = getMarkerIconForThemeAndSiteId(theme, marker.siteId);
          return addMarkerToMap(marker, markerIcon, infoWindow.current, mapReference.current, locale, t);
        })
        .filter((marker): marker is google.maps.Marker => !!marker);

      new MarkerClusterer({ markers: markersReference.current, map: mapReference.current, renderer: clusterRenderer });
    }

    return () => {
      if (markersReference.current) {
        for (const marker of markersReference.current) marker.setMap(null);
        markersReference.current = undefined;
      }
    };
  }, [locale, markers, t, theme]);

  return <div className={styles.map} ref={domElementReference} />;
};

const addMarkerToMap = (
  marker: NonNullable<MapComponentProperties['markers']>[number],
  markerIcon: string,
  infoWindow: google.maps.InfoWindow,
  map?: google.maps.Map,
  locale?: string,
  t?: any
) => {
  if (!map || !marker.lat || !marker.lng) return;
  // Note that since this has to be plain HTML, it does not hook into the router of next and cannot use imported classnames...
  // Navigating to a project page will cause a full page reload, and does not take i18n in consideration.
  // TODO: add localization to link and label
  const contentString = `
    <div class="infowindow">
      ${marker.imageUrl ? `<img src="${marker.imageUrl}?format=auto&w=400" alt="${marker.name}" />` : ''}
      <div class="content">
        ${marker.locationName ? `<span>${marker.locationName}</span>` : ''}
        <h2>${marker.name}</h2>
        ${
          marker.visitingAddress
            ? `
            <h3>${t('projects_detail_visiting_address')}</h3>
            <span>${marker.visitingAddress.streetAndNumber}</span> 
            <span>${marker.visitingAddress.postalCode} ${marker.visitingAddress.city}</span><br>`
            : ''
        }
        ${marker.description ? `<p><i>${marker.description}</i></p>` : ''}

        ${
          marker.postAddress
            ? `
            <h3>${t('projects_detail_post_address')}</h3>
            <span>${marker.postAddress?.mailBox || ''}</span> 
            <span>${marker.postAddress?.postalCode || ''} ${marker.postAddress?.city || ''}</span><br><br>`
            : ''
        }
        ${
          marker.contactDetails
            ? `
            <div class='contactDetails'>
            <h3>${t('projects_detail_contact_details')}</h3>
            <a href=tel:${marker.contactDetails.phone}>${marker.contactDetails.phone || ''}</a> 
            <a href=mailto:${marker.contactDetails.email}>${marker.contactDetails?.email}</a>
            </div>`
            : ''
        }
        ${
          marker.slug
            ? `<a href="${createHref({
                type: 'reference',
                href: { slug: { current: marker.slug }, _type: 'corporate-project' },
                locale,
              })}">${t('projects_detail_view_project')}</a>`
            : ''
        }
       
      </div>
    </div>`;

  const markerElement = new window.google.maps.Marker({
    position: { lat: marker.lat, lng: marker.lng },
    icon: markerIcon,
    map,
    title: marker.name,
  });

  markerElement.addListener('click', () => {
    if (infoWindow) {
      infoWindow.close();
    }

    infoWindow.setContent(contentString);
    infoWindow.open({ anchor: markerElement, map });
  });

  return markerElement;
};

function getMarkerIconForThemeAndSiteId(theme: SiteTheme, siteId?: SiteIdType, emptyMarker = false) {
  const selector = theme === 'holding' ? siteId : theme;
  return emptyMarker ? `/static/map-marker-${selector}-empty.png` : `/static/map-marker-${selector}.png`;
}
